import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/useAuth';

/**
 * TEMPORANEO: auto-logout disattivato — gli orari dei turni sono cambiati.
 * Rimettere a `true` dopo aver aggiornato LOGOUT_TIMES.
 */
const AUTO_LOGOUT_ENABLED = false;

/** Scheduled logout times (HH:MM, 24h format) aligned with shift boundaries. */
const LOGOUT_TIMES = ['11:10', '16:10', '21:10'];
const WARNING_ADVANCE_MS = 2 * 60 * 1000; // 2 min before
const SNOOZE_MS = 5 * 60 * 1000;          // 5 min
const SUSPEND_MS = 30 * 60 * 1000;        // 30 min

function getNextLogoutTime(): Date {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  for (const time of LOGOUT_TIMES) {
    const [h, m] = time.split(':').map(Number);
    const target = new Date(today.getTime());
    target.setHours(h, m, 0, 0);
    if (target.getTime() > now.getTime()) return target;
  }

  // All times passed today — first time tomorrow
  const [h, m] = LOGOUT_TIMES[0].split(':').map(Number);
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  tomorrow.setHours(h, m, 0, 0);
  return tomorrow;
}

const NOOP_RESULT = {
  showWarning: false,
  secondsRemaining: 0,
  snooze: () => {},
  suspend: () => {},
  logoutNow: () => {},
} as const;

/**
 * Scheduled auto-logout hook. Only active for non-super-admin users who
 * are currently on /shop (operators working the shop shift).
 *
 * Excluded contexts (by architecture — MainLayout doesn't wrap them):
 * - PWA routes (/pwa/*)
 * - Kiosk routes (/kiosk/*)
 *
 * Computes the next logout time from LOGOUT_TIMES, shows a warning dialog
 * 2 minutes before, and triggers signOut() at deadline.
 */
export function useAutoLogout() {
  const { signOut, user, hasRole } = useAuth();
  const location = useLocation();
  const nextLogoutRef = useRef<Date>(getNextLogoutTime());
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const loggingOutRef = useRef(false);
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const isOnShopRef = useRef(location.pathname.startsWith('/shop'));

  // Track current location — only active while on /shop
  useEffect(() => {
    const onShop = location.pathname.startsWith('/shop');
    isOnShopRef.current = onShop;
    // Hide warning immediately when navigating away from /shop
    if (!onShop) {
      setShowWarning(false);
    }
  }, [location.pathname]);

  const isSuperAdmin = hasRole('super-admin');

  const snooze = useCallback(() => {
    nextLogoutRef.current = new Date(nextLogoutRef.current.getTime() + SNOOZE_MS);
    setShowWarning(false);
  }, []);

  const suspend = useCallback(() => {
    nextLogoutRef.current = new Date(nextLogoutRef.current.getTime() + SUSPEND_MS);
    setShowWarning(false);
  }, []);

  const logoutNow = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    signOutRef.current();
  }, []);

  useEffect(() => {
    // Only active for non-super-admin users on /shop
    if (!AUTO_LOGOUT_ENABLED || !user || isSuperAdmin) return;

    const id = setInterval(() => {
      // Only trigger when user is currently on /shop
      if (!isOnShopRef.current) return;

      const now = Date.now();
      const target = nextLogoutRef.current.getTime();
      const diff = target - now;

      if (diff <= 0) {
        clearInterval(id);
        logoutNow();
        return;
      }

      if (diff <= WARNING_ADVANCE_MS) {
        setShowWarning(true);
        setSecondsRemaining(Math.ceil(diff / 1000));
      } else {
        setShowWarning(false);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [user, isSuperAdmin, logoutNow]);

  // Return noops when inactive (feature disattivata o super-admin)
  if (!AUTO_LOGOUT_ENABLED || isSuperAdmin) return NOOP_RESULT;

  return { showWarning, secondsRemaining, snooze, suspend, logoutNow };
}
